import { Modal, Form, Row, Col } from 'antd';
import lodashValues from 'lodash/values';
import zipObjectDeep from 'lodash/zipObjectDeep';
import { useTranslation } from 'react-i18next';
import crudSelectors from '@redux/crudSelectors';
import { useDispatch, useSelector } from '@redux';
import { DynamicObjectType } from 'types/commons';
import { RestInputContext } from 'components/form/RestInputContext';
import FormItem from 'components/form/FormItem';
import { editOrdersExchange } from '@redux/ordersExchange/actions';
import { validateRegex } from 'utils/validator';
import VNCitySelector from '../VNCitySelector';

interface IEditShippingAddressModalProps {
  visible: boolean;
  onCancel: () => void;
  customHandleSubmit?: any;
  title?: string;
  name?: string;
  rootName?: string;
}

const EditShippingAddressModal = ({
  visible,
  onCancel,
  customHandleSubmit,
  title,
  name,
  rootName,
}: IEditShippingAddressModalProps) => {
  const { t } = useTranslation();
  const record = useSelector<DynamicObjectType>(
    (state) => state.ordersExchange.currentData,
  );

  const cities = useSelector<DynamicObjectType>(
    (state) => state.config.address,
  );
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const loading = useSelector(
    crudSelectors.ordersExchange.getLoadingCurrentRecord,
  );

  const handleSubmit = (values) => {
    const payload: DynamicObjectType = zipObjectDeep(
      Object.keys(values || {}),
      lodashValues(values),
    );
    const province = cities?.find((e) => e.id === payload?.address?.provinceId);
    const district = province?.districts?.find(
      (e) => e.id === payload?.address?.districtId,
    );
    const ward = district?.wards?.find(
      (e) => e.id === payload?.address?.wardId,
    );
    if (customHandleSubmit) {
      customHandleSubmit({
        data: {
          id: record?.id,
          data: {
            address: {
              ...payload.address,
              ward: ward?.name,
              province: province?.name,
              district: district?.name,
            },
            packageInfo: payload.packageInfo,
          },
        },
        options: {},
      }).then(() => {
        onCancel();
        form.resetFields();
      });
      return;
    }
    dispatch(
      editOrdersExchange({
        data: {
          id: record?.id,
          data: {
            address: {
              ...payload.address,
              ward: ward?.name,
              province: province?.name,
              district: district?.name,
            },
            packageInfo: payload.packageInfo,
          },
        },
        options: {},
      }),
    ).then(() => {
      onCancel();
      form.resetFields();
    });
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title={title || t('orderDetail.editShippingInfo')}
      onOk={() => form.submit()}
      width={700}
      okButtonProps={{
        loading,
      }}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        initialValues={record?.[name]}
      >
        <RestInputContext.Provider value={{ form }}>
          <Row gutter={[14, 14]}>
            <Col span={12}>
              <FormItem
                name={'firstName'}
                label={t('common.input.surname.label')}
                required
                rootName={rootName}
              />
            </Col>
            <Col span={12}>
              <FormItem
                name={['lastName']}
                label={t('common.input.name.label')}
                required
                rootName={rootName}
              />
            </Col>
            <Col span={12}>
              <FormItem
                name={['telephone']}
                label={t('shippingInfo.telephone')}
                required
                rootName={rootName}
                rules={[
                  {
                    pattern: validateRegex.phoneHasPrefix,
                    message: t('orders.phone.validateMsg.invalid'),
                  },
                ]}
              />
            </Col>
            <Col span={12}>
              <FormItem
                name={['email']}
                label={t('shippingInfo.email')}
                required
                rootName={rootName}
              />
            </Col>
            <VNCitySelector rootSource={rootName} required form={form} />

            <Col span={24}>
              <FormItem
                name={['addressLine1']}
                label={t('Address')}
                required
                rootName={rootName}
              />
            </Col>
            <Col span={24}>
              <FormItem
                name={['addressLine2']}
                label={t('Address Name (Office, House name)')}
                rootName={rootName}
              />
            </Col>
          </Row>
        </RestInputContext.Provider>
      </Form>
    </Modal>
  );
};

export default EditShippingAddressModal;
